import { getDutyGenerationStats, queryKeys } from "@dutyon/apis";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Grid,
  Group,
  Loader,
  LoadingOverlay,
  Modal,
  Paper,
  Progress,
  SegmentedControl,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  TitleContainer,
  Tooltip,
} from "@dutyon/ui-kit";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  AlertCircle,
  BarChart3,
  Building2,
  Calendar,
  CheckCircle2,
  HelpCircle,
  Layers,
  RefreshCw,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { useMemo, useState } from "react";

export function DutyGenerationStatisticsPage() {
  const [periodKey, setPeriodKey] = useState<string>("30d");
  const [guideModalOpen, setGuideModalOpen] = useState<boolean>(false);

  const todayKst = new Date(Date.now() + 9 * 3600000).toISOString().slice(0, 10);
  const queryParams = useMemo(() => {
    const today = todayKst;
    switch (periodKey) {
      case "7d":
        return {
          startDate: dayjs(today).subtract(6, "day").format("YYYY-MM-DD"),
          endDate: today,
        };
      case "month":
        return {
          startDate: dayjs(today).startOf("month").format("YYYY-MM-DD"),
          endDate: today,
        };
      case "90d":
        return {
          startDate: dayjs(today).subtract(89, "day").format("YYYY-MM-DD"),
          endDate: today,
        };
      default:
        return {
          startDate: dayjs(today).subtract(29, "day").format("YYYY-MM-DD"),
          endDate: today,
        };
    }
  }, [periodKey, todayKst]);

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: queryKeys.statistics.dutyGeneration(queryParams),
    queryFn: () => getDutyGenerationStats(queryParams),
    refetchInterval: 60_000,
    retry: false,
  });

  const maxDailyGenerations = useMemo(() => {
    if (!data?.timeline?.length) return 1;
    return Math.max(...data.timeline.map((item) => item.generationCount), 1);
  }, [data?.timeline]);

  return (
    <TitleContainer
      size="xl"
      title={
        <Group justify="space-between" align="center" wrap="wrap" style={{ width: "100%" }}>
          <Group gap="sm">
            <ThemeIcon size="lg" radius="md" variant="light" color="blue">
              <BarChart3 size={22} />
            </ThemeIcon>
            <Stack gap={2}>
              <Title order={3}>Duty 자동 생성 통계</Title>
              <Text size="xs" c="dimmed">
                병원별 생성표 저장 기록을 한국시간 기준으로 집계합니다.
              </Text>
            </Stack>
          </Group>
          <Group gap="xs">
            <SegmentedControl
              value={periodKey}
              onChange={setPeriodKey}
              size="xs"
              radius="md"
              data={[
                { label: "최근 7일", value: "7d" },
                { label: "최근 30일", value: "30d" },
                { label: "이번 달", value: "month" },
                { label: "최근 90일", value: "90d" },
              ]}
            />
            <Tooltip label="새로고침">
              <ActionIcon
                variant="default"
                size="input-xs"
                radius="md"
                onClick={() => refetch()}
                loading={isFetching}
              >
                <RefreshCw size={14} />
              </ActionIcon>
            </Tooltip>
            <Button
              variant="light"
              color="gray"
              size="xs"
              radius="md"
              leftSection={<HelpCircle size={14} />}
              onClick={() => setGuideModalOpen(true)}
            >
              집계 기준 안내
            </Button>
          </Group>
        </Group>
      }
    >
      <Stack gap="lg" style={{ position: "relative" }}>
        <LoadingOverlay visible={isLoading || isFetching} overlayProps={{ blur: 1 }} />

        {isError ? (
          <Alert title="통계를 확인하지 못했습니다" color="red" icon={<AlertCircle size={18} />}>
            통계 수집이 준비 중이거나 조회에 실패했습니다. 잠시 후 다시 조회해 주세요. 모의 숫자는
            표시하지 않습니다.
          </Alert>
        ) : data ? (
          <Alert title="내부 저장 기록 기준" color="teal" icon={<CheckCircle2 size={18} />}>
            생성표 저장이 성공한 기록만 집계합니다. GPU 계산 시도·실패나 일반 수동 저장은 포함하지
            않습니다. 과거 외부 분석 통계는 합산하지 않으며 수집 적용 이전 기록은 포함되지 않습니다.
            {data.firstRecordedAt
              ? ` 최초 보관 기록: ${new Date(data.firstRecordedAt).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" })} (한국시간).`
              : " 아직 저장 기록이 없습니다."}
          </Alert>
        ) : null}

        {data && !isError && (
          <>
            {/* 요약 KPI 카드 */}
            <Grid gutter="md">
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <Card withBorder radius="md" p="lg">
                  <Group justify="space-between" align="flex-start">
                    <Stack gap={4}>
                      <Text size="xs" c="dimmed" fw={700}>
                        생성표 저장 성공 횟수
                      </Text>
                      <Title order={2} c="blue">
                        {(data?.summary.totalGenerations ?? 0).toLocaleString()}
                        <Text span size="sm" c="dimmed" fw={500} ml={4}>
                          회
                        </Text>
                      </Title>
                    </Stack>
                    <ThemeIcon size={44} radius="md" color="blue" variant="light">
                      <Sparkles size={22} />
                    </ThemeIcon>
                  </Group>
                  <Text size="xs" c="dimmed" mt="xs">
                    조회 기간 동안 기록된 생성표 저장 성공 건수
                  </Text>
                </Card>
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 4 }}>
                <Card withBorder radius="md" p="lg">
                  <Group justify="space-between" align="flex-start">
                    <Stack gap={4}>
                      <Text size="xs" c="dimmed" fw={700}>
                        저장된 근무 칸 수
                      </Text>
                      <Title order={2} c="teal">
                        {(data?.summary.totalDuties ?? 0).toLocaleString()}
                        <Text span size="sm" c="dimmed" fw={500} ml={4}>
                          개
                        </Text>
                      </Title>
                    </Stack>
                    <ThemeIcon size={44} radius="md" color="teal" variant="light">
                      <Layers size={22} />
                    </ThemeIcon>
                  </Group>
                  <Text size="xs" c="dimmed" mt="xs">
                    간호사와 날짜별로 저장한 근무 칸 수
                  </Text>
                </Card>
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 4 }}>
                <Card withBorder radius="md" p="lg">
                  <Group justify="space-between" align="flex-start">
                    <Stack gap={4}>
                      <Text size="xs" c="dimmed" fw={700}>
                        기능 이용 병원 수
                      </Text>
                      <Title order={2} c="indigo">
                        {(data?.summary.activeCompaniesCount ?? 0).toLocaleString()}
                        <Text span size="sm" c="dimmed" fw={500} ml={4}>
                          곳
                        </Text>
                      </Title>
                    </Stack>
                    <ThemeIcon size={44} radius="md" color="indigo" variant="light">
                      <Building2 size={22} />
                    </ThemeIcon>
                  </Group>
                  <Text size="xs" c="dimmed" mt="xs">
                    해당 기간에 생성표 저장 성공 기록이 있는 병원 수
                  </Text>
                </Card>
              </Grid.Col>
            </Grid>

            {/* 시간순 추이 차트 */}
            <Card withBorder radius="md" p="lg">
              <Group justify="space-between" mb="md">
                <Group gap="xs">
                  <TrendingUp size={18} />
                  <Title order={4}>일자별 생성표 저장 추이</Title>
                </Group>
                <Text size="xs" c="dimmed">
                  {queryParams.startDate} ~ {queryParams.endDate}
                </Text>
              </Group>

              {data?.timeline && data.timeline.length > 0 ? (
                <Stack gap="xs" mt="md">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      gap: 6,
                      height: 180,
                      paddingBottom: 24,
                      overflowX: "auto",
                      borderBottom: "1px solid var(--mantine-color-gray-3)",
                    }}
                  >
                    {data.timeline.map((day) => {
                      const heightPercent =
                        day.generationCount > 0
                          ? Math.max(
                              Math.round((day.generationCount / maxDailyGenerations) * 100),
                              8,
                            )
                          : 2;

                      return (
                        <Tooltip
                          key={day.date}
                          label={
                            <Stack gap={2} p={2}>
                              <Text fw={700} size="xs">
                                {day.date}
                              </Text>
                              <Text size="xs">저장 성공: {day.generationCount}회</Text>
                              <Text size="xs">
                                저장 근무 칸: {day.dutyCount.toLocaleString()}개
                              </Text>
                              {day.companies.length > 0 && (
                                <Text size="xs" c="dimmed">
                                  병원:{" "}
                                  {day.companies
                                    .map((c) => `${c.companyName}(${c.count})`)
                                    .join(", ")}
                                </Text>
                              )}
                            </Stack>
                          }
                          withArrow
                        >
                          <div
                            style={{
                              flex: 1,
                              minWidth: 16,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              height: "100%",
                              justifyContent: "flex-end",
                              cursor: "pointer",
                            }}
                          >
                            <div
                              style={{
                                width: "100%",
                                maxWidth: 28,
                                height: `${heightPercent}%`,
                                backgroundColor:
                                  day.generationCount > 0
                                    ? "var(--mantine-color-blue-6)"
                                    : "var(--mantine-color-gray-2)",
                                borderRadius: "4px 4px 0 0",
                                transition: "height 0.3s ease, background-color 0.2s",
                              }}
                            />
                            <Text
                              size="9px"
                              c="dimmed"
                              style={{
                                marginTop: 4,
                                whiteSpace: "nowrap",
                                transform: data.timeline.length > 15 ? "rotate(-45deg)" : "none",
                                transformOrigin: "center top",
                              }}
                            >
                              {day.date.slice(5)}
                            </Text>
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                  <Group justify="flex-end" gap="lg" mt="xs">
                    <Group gap={6}>
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          backgroundColor: "var(--mantine-color-blue-6)",
                          borderRadius: 2,
                        }}
                      />
                      <Text size="xs" c="dimmed">
                        저장 성공
                      </Text>
                    </Group>
                  </Group>
                </Stack>
              ) : (
                <Paper p="xl" withBorder radius="md" style={{ textAlign: "center" }}>
                  <Text c="dimmed" size="sm">
                    선택한 기간 동안의 저장 기록이 없습니다.
                  </Text>
                </Paper>
              )}
            </Card>

            {/* 병원별 생성 순위 및 상세 테이블 */}
            <Card withBorder radius="md" p="lg">
              <Group justify="space-between" mb="md">
                <Group gap="xs">
                  <Building2 size={18} />
                  <Title order={4}>병원별 생성표 저장 현황</Title>
                </Group>
                <Badge variant="light" color="blue">
                  총 {data?.companies.length ?? 0}개 병원
                </Badge>
              </Group>

              {isLoading ? (
                <Table.ScrollContainer minWidth={800}>
                  <Table highlightOnHover verticalSpacing="sm">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th style={{ width: 60 }}>순위</Table.Th>
                        <Table.Th>병원명</Table.Th>
                        <Table.Th style={{ width: 140 }}>저장 횟수</Table.Th>
                        <Table.Th style={{ width: 150 }}>저장 근무 칸 수</Table.Th>
                        <Table.Th style={{ width: 220 }}>점유율 비중</Table.Th>
                        <Table.Th style={{ width: 140 }}>최근 저장일</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      <Table.Tr>
                        <Table.Td colSpan={6} align="center" py="xl">
                          <Stack align="center" gap="xs">
                            <Loader size="sm" />
                            <Text size="sm" c="dimmed">
                              병원별 통계 데이터를 불러오는 중입니다...
                            </Text>
                          </Stack>
                        </Table.Td>
                      </Table.Tr>
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              ) : data?.companies && data.companies.length > 0 ? (
                <Table.ScrollContainer minWidth={800}>
                  <Table highlightOnHover verticalSpacing="sm">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th style={{ width: 60 }}>순위</Table.Th>
                        <Table.Th>병원명</Table.Th>
                        <Table.Th style={{ width: 140 }}>저장 횟수</Table.Th>
                        <Table.Th style={{ width: 150 }}>저장 근무 칸 수</Table.Th>
                        <Table.Th style={{ width: 220 }}>점유율 비중</Table.Th>
                        <Table.Th style={{ width: 140 }}>최근 저장일</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {data.companies.map((company, index) => {
                        const totalGen = data.summary.totalGenerations || 1;
                        const sharePercentage = Math.round(
                          (company.generationCount / totalGen) * 100,
                        );

                        return (
                          <Table.Tr key={company.companyId}>
                            <Table.Td>
                              <Badge
                                size="sm"
                                variant={index < 3 ? "filled" : "light"}
                                color={
                                  index === 0
                                    ? "yellow"
                                    : index === 1
                                      ? "gray"
                                      : index === 2
                                        ? "orange"
                                        : "gray"
                                }
                              >
                                {index + 1}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              <Text fw={600} size="sm">
                                {company.companyName}
                              </Text>
                              <Text size="xs" c="dimmed">
                                {company.companyId}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Badge variant="light" color="blue" size="md">
                                {company.generationCount.toLocaleString()}회
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm" fw={500}>
                                {company.dutyCount.toLocaleString()}개
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Stack gap={4}>
                                <Group justify="space-between">
                                  <Text size="xs" c="dimmed">
                                    비중
                                  </Text>
                                  <Text size="xs" fw={700}>
                                    {sharePercentage}%
                                  </Text>
                                </Group>
                                <Progress
                                  value={sharePercentage}
                                  color="blue"
                                  size="sm"
                                  radius="xl"
                                />
                              </Stack>
                            </Table.Td>
                            <Table.Td>
                              <Group gap={4}>
                                <Calendar size={13} color="gray" />
                                <Text size="xs" c="dimmed">
                                  {company.lastGeneratedAt || "-"}
                                </Text>
                              </Group>
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              ) : (
                <Paper p="xl" withBorder radius="md" style={{ textAlign: "center" }}>
                  <Text c="dimmed" size="sm">
                    조회된 병원별 데이터가 없습니다.
                  </Text>
                </Paper>
              )}
            </Card>
          </>
        )}
        <Modal
          opened={guideModalOpen}
          onClose={() => setGuideModalOpen(false)}
          title="집계 기준 안내"
          size="lg"
        >
          <Stack gap="md">
            <Text>
              대시보드에서 생성한 표를 실제 근무표에 저장하면 성공한 저장 1건으로 집계합니다. 같은
              저장 요청의 재전송은 중복 집계하지 않습니다. 다른 요청으로 다시 저장한 경우에는 새
              저장으로 집계합니다.
            </Text>
            <Text>
              근무 칸 수는 간호사와 날짜별로 저장한 칸의 수입니다. 예를 들어 간호사 2명의 3일 표는
              저장 1회, 근무 6칸입니다. 간호사 행 수를 세던 이전 외부 통계와 수치 기준이 다릅니다.
            </Text>
            <Text>
              아직 저장하지 않은 GPU 계산 성공, 일반 수동 저장, 실패·취소는 포함하지 않습니다. 이후
              근무표를 수정하거나 지워도 이미 성공한 저장 기록의 횟수는 변하지 않습니다.
            </Text>
            <Text>
              기존 표에서 과거 생성 횟수를 추측하거나 외부 통계를 합쳐 채우지 않습니다. 내부 수집
              적용 이후 보관된 기록만 표시합니다. 병원 이름은 현재 이름을 사용하며, 병원이 물리
              삭제되면 해당 병원의 집계 기록도 삭제됩니다.
            </Text>
            <Button onClick={() => setGuideModalOpen(false)}>확인</Button>
          </Stack>
        </Modal>
      </Stack>
    </TitleContainer>
  );
}
